import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CreditCard, ArrowRight, Check, AlertTriangle, CheckCircle, AlertCircle, ExternalLink } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";

// Direct supabase client for admin operations (use this instead of API endpoint)
import { createClient } from '@supabase/supabase-js';

// Replace with your Stripe client ID from the Stripe Connect dashboard
const STRIPE_CLIENT_ID = 'ca_RwDBZwVPx2fCIrEfRuF37nz656FSS8rC'; // ✅ LIVE MODE CLIENT ID 

const FloristStripe = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading, isFlorist } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [accountStatus, setAccountStatus] = useState({
    connected: false,
    accountId: null
  });

  // Check for URL parameters that indicate redirect from Stripe
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const success = params.get('success');
    const refresh = params.get('refresh');
    const session_complete = params.get('session_complete');
    
    if (success === 'true' || session_complete === 'true') {
      console.log('Detected successful return from Stripe onboarding');
      toast({
        title: "Success",
        description: "Your Stripe account has been connected successfully.",
      });
      // Force immediate status check
      checkStripeStatus(true);
    } else if (refresh === 'true') {
      console.log('Detected refresh request from Stripe');
      checkStripeStatus(true);
    }
  }, [location.search]);

  // Check if the user is a florist, redirect if not
  useEffect(() => {
    if (!authLoading && user && !isFlorist) {
      navigate("/");
    }
  }, [authLoading, user, isFlorist, navigate]);

  // Handle OAuth redirect with code
  useEffect(() => {
    const handleOAuthRedirect = async () => {
      const code = searchParams.get('code');
      
      if (code) {
        setConnecting(true);
        try {
          // In a production app, you would exchange this code for an account ID
          // using a serverless function or backend API
          toast({
            title: 'Connected!',
            description: 'Processing your Stripe connection...',
          });
          
          // For now, we'll just refresh the page to check account status
          // Remove the code from the URL to prevent reusing it
          navigate('/florist-dashboard/stripe', { replace: true });
        } catch (error) {
          console.error('Error connecting Stripe account:', error);
          toast({
            title: 'Connection failed',
            description: 'There was a problem connecting your Stripe account.',
            variant: "destructive",
          });
        } finally {
          setConnecting(false);
        }
      }
    };
    
    if (user && !authLoading) {
      handleOAuthRedirect();
    }
  }, [user, authLoading, searchParams, toast, navigate]);

  // Check if the store already has a Stripe account
  useEffect(() => {
    const checkStripeStatus = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        // Get the store profile for the current user
        const { data, error } = await supabase
          .from('store_profiles')
          .select('stripe_account_id')
          .eq('florist_id', user.id)
          .single();
          
        if (error) throw error;
        
        setAccountStatus({
          connected: !!data.stripe_account_id,
          accountId: data.stripe_account_id
        });
      } catch (error) {
        console.error('Error checking Stripe status:', error);
        toast({
          title: 'Error',
          description: 'Could not check your Stripe connection status.',
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    if (user && !authLoading) {
      checkStripeStatus();
    }
  }, [user, authLoading, toast]);

  // Function to disconnect Stripe account
  const disconnectStripeAccount = async () => {
    if (!user?.id) return;
    
    setDisconnecting(true);
    try {
      // Simply remove the Stripe account ID from the store profile
      const { error } = await supabase
        .from('store_profiles')
        .update({ stripe_account_id: null })
        .eq('florist_id', user.id);
        
      if (error) throw error;
      
      setAccountStatus({
        connected: false,
        accountId: null
      });
      
      toast({
        title: 'Disconnected',
        description: 'Your Stripe account has been disconnected.',
      });
    } catch (error) {
      console.error('Error disconnecting Stripe:', error);
      toast({
        title: 'Error',
        description: 'Could not disconnect your Stripe account.',
        variant: "destructive",
      });
    } finally {
      setDisconnecting(false);
    }
  };

  // Connect with Stripe - Direct OAuth URL method
  const connectWithStripe = () => {
    const redirectUri = `${window.location.origin}/florist-dashboard/stripe`;
    const stripeConnectUrl = `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${STRIPE_CLIENT_ID}&scope=read_write&redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = stripeConnectUrl;
  };

  // Go to Stripe Express dashboard
  const goToStripeDashboard = () => {
    if (accountStatus.accountId) {
      // accountId already includes 'acct_' prefix, so just use it directly
      window.open(`https://connect.stripe.com/app/express#${accountStatus.accountId}/overview`, '_blank');
    } else {
      window.open('https://dashboard.stripe.com/dashboard', '_blank');
    }
  };

  if (authLoading || !user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAF7F0] pt-20">
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-xl mx-auto">
          <h1 className="text-3xl font-bold text-brand-secondary mb-6">Stripe Connect</h1>
          <p className="text-brand-secondary/70 mb-8">
            Connect your Stripe account to receive payments directly from customers. BloomSpace takes a small commission on each order.
          </p>
          
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Payment Processing Status</CardTitle>
              <CardDescription>
                Your Stripe Connect account status and payment capabilities
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
                  <span className="ml-3 text-brand-secondary">Checking Stripe status...</span>
                </div>
              ) : accountStatus.connected ? (
                <div className="space-y-6">
                  <div className="flex items-center">
                    <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
                    <div>
                      <h3 className="font-medium text-lg text-brand-secondary">
                        Your Stripe account is connected
                      </h3>
                      <p className="text-brand-secondary/70">
                        You can now receive payments from customers
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-[#F8F7F4] rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <Check className="h-4 w-4 text-green-500 mr-2" />
                        <span className="font-medium">Account Verified</span>
                      </div>
                      <p className="text-sm text-brand-secondary/70">
                        Your Stripe account has been verified
                      </p>
                    </div>
                    
                    <div className="bg-[#F8F7F4] rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <Check className="h-4 w-4 text-green-500 mr-2" />
                        <span className="font-medium">Payments Enabled</span>
                      </div>
                      <p className="text-sm text-brand-secondary/70">
                        You can accept payments from customers
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center">
                    <AlertTriangle className="h-8 w-8 text-amber-500 mr-3" />
                    <div>
                      <h3 className="font-medium text-lg text-brand-secondary">
                        Your Stripe account is not connected
                      </h3>
                      <p className="text-brand-secondary/70">
                        Connect your Stripe account to receive payments from customers
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <Button 
                      onClick={connectWithStripe}
                      disabled={connecting}
                      className="bg-[#967F81] hover:bg-[#E33D16] text-white"
                    >
                      {connecting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Connect Stripe
                    </Button>
                    
                    {connecting && (
                      <p className="text-sm text-gray-500 mt-2">
                        You'll be redirected to Stripe to complete the connection process.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex flex-col sm:flex-row sm:justify-between gap-3">
              {accountStatus.connected && (
                <>
                  <Button 
                    className="w-full sm:w-auto bg-[#967F81] hover:bg-[#E33D16] text-white"
                    onClick={goToStripeDashboard}
                  >
                    Go to Stripe Dashboard
                  </Button>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full sm:w-auto border-red-300 text-red-500 hover:bg-red-50 hover:text-red-600"
                        onClick={disconnectStripeAccount}
                        disabled={disconnecting}
                      >
                        {disconnecting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Disconnect Stripe
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Disconnect Stripe Account</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to disconnect your Stripe account? 
                          This will prevent you from receiving payments until you reconnect.
                          Any existing orders will not be affected.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={disconnectStripeAccount}
                          className="bg-red-500 hover:bg-red-600 text-white"
                        >
                          Disconnect
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </CardFooter>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>About Stripe Connect</CardTitle>
              <CardDescription>
                How payments work on BloomSpace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-brand-secondary">
                BloomSpace uses Stripe Connect to handle payments securely:
              </p>
              <ul className="space-y-2 list-disc pl-5 text-brand-secondary/80">
                <li>Customer payments are sent directly to your Stripe account</li>
                <li>BloomSpace takes a small commission fee for each transaction</li>
                <li>You retain control of your funds and can set up payouts to your bank account</li>
                <li>All refunds and disputes are handled between you and your customers</li>
                <li>You can view detailed sales reports and transaction history in the Stripe Dashboard</li>
              </ul>
              <div className="mt-4 bg-[#F8F7F4] p-4 rounded-md">
                <p className="text-brand-secondary">
                  <strong>Important:</strong> Please review our comprehensive guide on payments, refunds, and payouts to understand your responsibilities as a florist on BloomSpace.
                </p>
                <Button 
                  variant="outline" 
                  className="mt-3"
                  onClick={() => navigate("/florist-dashboard/stripe/terms")}
                >
                  View Payment Terms & Conditions
                </Button>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                variant="outline" 
                className="w-full sm:w-auto"
                onClick={() => navigate("/florist-dashboard")}
              >
                Back to Dashboard
              </Button>
            </CardFooter>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default FloristStripe; 